/**
 * AI-log per project (23 okt 2026): welke aangemelde AI-tool is waarvoor gebruikt, met prompt/versie,
 * uren met AI en geschatte uren traditioneel. Basis voor de kwartaalrapportage aan de klant
 * (Admin → Klantvereisten). Klein en ingeklapt in het klantvereisten-blok op de projectpagina.
 *
 * Vangnetten: toevoegen = gewoon opslaan; verwijderen = bevestigen (alleen invoerder of beheer).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../utils/apiConfig';
import { authFetch } from '../utils/authHelpers';
import { leesJson, meldFout } from '../utils/apiFout';
import { bevestig } from '../utils/bevestig';

const FONT = '"Inter", system-ui, sans-serif';
const STIL = '#767c7e';
const vandaag = () => new Date().toLocaleDateString('sv-SE');   // lokale datum, JJJJ-MM-DD
const LEEG = { datum: '', tool: '', doel: '', promptVersie: '', wie: '', urenAi: '', urenTraditioneel: '' };
const veld = { size: 'small', InputLabelProps: { shrink: true }, sx: { '& .MuiInputBase-input': { fontSize: '0.82rem' } } };

export default function AiLogPaneel({ projectId, nummer, onGewijzigd }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [bezig, setBezig] = useState(false);

  // Alleen het antwoord van het laatste verzoek telt (projectwissel tijdens laden, Codex 23 okt).
  const verzoek = useRef(0);
  const laad = useCallback(async () => {
    const nr = ++verzoek.current;
    const q = new URLSearchParams();
    if (projectId) q.set('projectId', projectId);
    if (nummer) q.set('nummer', nummer);
    try {
      const d = await leesJson(await authFetch(`${BACKEND_URL}/Api/klantvereisten/ai-log?${q}`), 'AI-log laden');
      if (nr === verzoek.current) setData(d);
    } catch (e) {
      if (nr !== verzoek.current) return;
      meldFout(e, 'AI-log laden');
      setData({ regels: [], tools: [], fout: true });
    }
  }, [projectId, nummer]);

  useEffect(() => { setData(null); setForm(null); laad(); }, [laad]);

  const zet = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const bewaar = async () => {
    setBezig(true);
    try {
      await leesJson(await authFetch(`${BACKEND_URL}/Api/klantvereisten/ai-log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, projectId, projectNummer: nummer }),
      }), 'AI-gebruik vastleggen');
      toast.success('AI-gebruik vastgelegd');
      setForm(null);
      await laad();
      onGewijzigd?.();
    } catch (e) {
      meldFout(e, 'AI-gebruik vastleggen');
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (r) => {
    const ok = await bevestig({
      titel: 'Regel uit het AI-log verwijderen?',
      tekst: `${r.tool} op ${r.datum} (${r.doel}) verdwijnt uit het log en uit de kwartaalrapportage.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      await leesJson(await authFetch(`${BACKEND_URL}/Api/klantvereisten/ai-log/${r.id}`, { method: 'DELETE' }), 'Verwijderen');
      await laad();
      onGewijzigd?.();
    } catch (e) {
      meldFout(e, 'Verwijderen');
    }
  };

  if (!data) return <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: STIL, mt: 1 }}>AI-log laden…</Typography>;

  const regels = data.regels || [];
  return (
    <Box sx={{ mt: 1.5, maxWidth: 760 }}>
      {regels.length === 0 && !data.fout && (
        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: STIL }}>
          Nog niets gelogd. Leg elk gebruik van een AI-tool vast: tool, waarvoor, prompt of versie, en de uren.
        </Typography>
      )}
      {regels.map((r) => (
        <Box key={r.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', py: 0.5, borderBottom: '1px solid #f0f2f3' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: STIL, minWidth: 82 }}>{r.datum}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#2d3335' }}>
              {r.tool} — {r.doel}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: STIL, overflowWrap: 'anywhere' }}>
              {[r.wie, `${r.urenAi} u met AI · ${r.urenTraditioneel} u traditioneel`, r.promptVersie].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Tooltip title="Verwijderen">
            <IconButton size="small" onClick={() => verwijder(r)} aria-label={`Regel ${r.tool} ${r.datum} verwijderen`}>
              <DeleteOutlineIcon sx={{ fontSize: 16, color: STIL }} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      {form ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr 1fr' }, gap: 1.25, mt: 1.5 }}>
          <TextField {...veld} type="date" label="Datum" value={form.datum} onChange={zet('datum')} inputProps={{ max: vandaag() }} />
          <TextField {...veld} select label="Tool" value={form.tool} onChange={zet('tool')}>
            {(data.tools || []).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField {...veld} label="Waarvoor" value={form.doel} onChange={zet('doel')} placeholder="bv. storyboardbeelden scène 2" />
          <TextField {...veld} label="Prompt / versie (tekst of link)" value={form.promptVersie} onChange={zet('promptVersie')}
            multiline minRows={1} maxRows={4} sx={{ ...veld.sx, gridColumn: { sm: '1 / span 2' } }} />
          <TextField {...veld} label="Wie" value={form.wie} onChange={zet('wie')} placeholder="leeg = jij" />
          <TextField {...veld} type="number" label="Uren met AI" value={form.urenAi} onChange={zet('urenAi')} inputProps={{ min: 0, step: 0.25 }} />
          <TextField {...veld} type="number" label="Geschatte uren traditioneel" value={form.urenTraditioneel}
            onChange={zet('urenTraditioneel')} inputProps={{ min: 0, step: 0.25 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" variant="contained" disableElevation onClick={bewaar} disabled={bezig || !form.tool || !form.doel}
              sx={{ textTransform: 'none' }}>
              {bezig ? 'Bezig…' : 'Vastleggen'}
            </Button>
            <Button size="small" onClick={() => setForm(null)} sx={{ textTransform: 'none', color: STIL }}>Annuleren</Button>
          </Box>
        </Box>
      ) : (
        <Button size="small" onClick={() => setForm({ ...LEEG, datum: vandaag() })} disabled={!!data.fout}
          sx={{ textTransform: 'none', mt: 1, px: 0, color: '#556071' }}>
          + AI-gebruik vastleggen
        </Button>
      )}
    </Box>
  );
}
